import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePlan, useRegeneratePlan, usePlanProgress } from '../hooks/usePlans';
import { useGoal, useUpdateGoal } from '../hooks/useGoals';
import { useAuthStore } from '../store/authStore';
import { useT } from '../hooks/useT';
import ScenarioChart from '../components/plans/ScenarioChart';
import AIInsights from '../components/plans/AIInsights';
import InvestmentEducation from '../components/plans/InvestmentEducation';
import GoalForm from '../components/goals/GoalForm';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import { toast } from '../store/toastStore';
import { exportPlanPdf } from '../utils/exportPdf';


function fmt(value, currency = 'BRL') {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return parseFloat(value || 0).toLocaleString(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
}

function SummaryCard({ label, value, sub, highlight }) {
  return (
    <div className={`card ${highlight ? 'border-brand-700/50 bg-brand-950/20' : ''}`}>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${highlight ? 'text-brand-400' : 'text-slate-100'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

const SIM_COLORS = ['text-sky-400', 'text-violet-400', 'text-amber-400', 'text-rose-400', 'text-emerald-400'];

function computeSimulationColumn(projection, monthlyRatePct) {
  const rate = monthlyRatePct / 100;
  const startSavings = parseFloat(projection[0].accumulated_value) - parseFloat(projection[0].monthly_addition);
  let balance = startSavings;
  return projection.map((row) => {
    const interest = parseFloat((balance * rate).toFixed(2));
    balance = parseFloat((balance + interest + parseFloat(row.monthly_addition)).toFixed(2));
    return { interest, accumulated: balance };
  });
}

/** Add N months to a date, keeping day=1 to avoid month-overflow edge cases. */
function addMonths(date, n) {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  return d;
}

/** "Mar/2026" */
function fmtMonthYear(date) {
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '/');
}

/**
 * Build a map: rowMonth (1-based) → cumulative savings as of end of that calendar month.
 * Uses the latest progress entry on or before the last day of each month.
 */
function buildSavingsMap(entries, planCreatedAt, count) {
  if (!entries?.length) return {};
  const map = {};
  for (let m = 1; m <= count; m++) {
    const rowDate = addMonths(new Date(planCreatedAt), m - 1);
    // last day of that calendar month
    const endOfMonth = new Date(rowDate.getFullYear(), rowDate.getMonth() + 1, 0, 23, 59, 59);
    let latest = null;
    for (const e of entries) {
      if (new Date(e.entry_date) <= endOfMonth) {
        latest = parseFloat(e.cumulative_savings);
      } else {
        break; // entries sorted ASC by entry_date
      }
    }
    if (latest !== null) map[m] = latest;
  }
  return map;
}

function MonthlyProjectionTable({ projection, simulations, currency, planCreatedAt, progressEntries }) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? projection : projection.slice(0, 6);
  const hasSims = simulations?.length > 0;
  const simData = hasSims
    ? simulations.map((sim) => computeSimulationColumn(projection, sim.monthly_rate_pct))
    : [];

  const savedMap = buildSavingsMap(progressEntries, planCreatedAt, projection.length);

  const now = new Date();
  const currentYM = now.getFullYear() * 100 + now.getMonth(); // comparable int

  function rowDate(month) {
    return planCreatedAt ? addMonths(new Date(planCreatedAt), month - 1) : null;
  }

  function isCurrentMonth(month) {
    const d = rowDate(month);
    if (!d) return false;
    return d.getFullYear() * 100 + d.getMonth() === currentYM;
  }

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="font-semibold text-slate-200">{t('plan.projection.title')}</h3>
        {hasSims && (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {simulations.map((sim, i) => (
              <span key={sim.name} className={`flex items-center gap-1 text-xs font-medium ${SIM_COLORS[i % SIM_COLORS.length]}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
                {sim.name} ({sim.annual_rate_pct}%/yr)
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-surface">
              <th className="pb-2 pr-3">{t('plan.projection.col.month')}</th>
              <th className="pb-2 pr-4">{t('plan.projection.col.date')}</th>
              <th className="pb-2 pr-4 text-right">{t('plan.projection.col.add')}</th>
              {hasSims ? (
                simulations.flatMap((sim, i) => [
                  <th key={`${sim.name}-int`} className={`pb-2 pr-4 text-right ${SIM_COLORS[i % SIM_COLORS.length]}`}>
                    {t('plan.projection.col.sim_interest').replace('{name}', sim.name)}
                  </th>,
                  <th key={`${sim.name}-acc`} className={`pb-2 pr-4 text-right font-semibold ${SIM_COLORS[i % SIM_COLORS.length]}`}>
                    {t('plan.projection.col.sim_total').replace('{name}', sim.name)}
                  </th>,
                ])
              ) : (
                <>
                  <th className="pb-2 pr-4 text-right text-slate-700">{t('plan.projection.col.interest')}</th>
                  <th className="pb-2 pr-4 text-right text-slate-500">{t('plan.projection.col.accumulated')}</th>
                </>
              )}
              <th className="pb-2 text-right text-green-500">{t('plan.projection.col.total_saved')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isCurrent = isCurrentMonth(row.month);
              const rd = rowDate(row.month);
              const savedAmt = savedMap[row.month];
              return (
                <tr
                  key={row.month}
                  className={`border-b border-surface/50 transition-colors ${
                    isCurrent
                      ? 'bg-brand-600/10 border-l-2 border-l-brand-500'
                      : 'hover:bg-surface/30'
                  }`}
                >
                  <td className={`py-2 pr-3 font-mono text-xs ${isCurrent ? 'text-brand-400 font-bold' : 'text-slate-500'}`}>
                    {row.month}
                  </td>
                  <td className={`py-2 pr-4 whitespace-nowrap ${isCurrent ? 'text-brand-300 font-semibold' : 'text-slate-400'}`}>
                    {rd ? fmtMonthYear(rd) : '—'}
                    {isCurrent && <span className="ml-1.5 text-[10px] bg-brand-600/40 text-brand-300 px-1.5 py-0.5 rounded-full align-middle">{t('plan.projection.now')}</span>}
                  </td>
                  <td className="py-2 pr-4 text-right text-slate-300">{fmt(row.monthly_addition, currency)}</td>
                  {hasSims ? (
                    simData.flatMap((col, i) => [
                      <td key={`${i}-int`} className={`py-2 pr-4 text-right ${SIM_COLORS[i % SIM_COLORS.length]}`}>
                        +{fmt(col[row.month - 1].interest, currency)}
                      </td>,
                      <td key={`${i}-acc`} className={`py-2 pr-4 text-right font-semibold ${SIM_COLORS[i % SIM_COLORS.length]}`}>
                        {fmt(col[row.month - 1].accumulated, currency)}
                      </td>,
                    ])
                  ) : (
                    <>
                      <td className="py-2 pr-4 text-right text-slate-700 text-xs italic">+0</td>
                      <td className="py-2 pr-4 text-right font-medium text-slate-100">{fmt(row.accumulated_value, currency)}</td>
                    </>
                  )}
                  <td className="py-2 text-right">
                    {savedAmt != null ? (
                      <span className="font-semibold text-green-400">{fmt(savedAmt, currency)}</span>
                    ) : (
                      <span className="text-slate-700 text-xs">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!hasSims && (
        <p className="mt-3 text-xs text-slate-600">{t('plan.projection.hint')}</p>
      )}
      {projection.length > 6 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-brand-400 hover:text-brand-300 transition-colors"
        >
          {expanded ? t('plan.projection.show_less') : t('plan.projection.show_all').replace('{n}', projection.length)}
        </button>
      )}
    </div>
  );
}

function ProgressSummaryCard({ planId, currency }) {
  const t = useT();
  const { data } = usePlanProgress(planId);
  const totalSaved = data?.total_saved ?? 0;
  const pct = data?.percent_complete ?? 0;
  const entries = data?.entries?.length ?? 0;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-200">{t('plan.progress.title')}</h3>
        <Link
          to={`/plans/${planId}/progress`}
          className="text-xs text-brand-400 hover:text-brand-300 transition-colors"
        >
          {t('plan.progress.history')}
        </Link>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between text-xs text-slate-500">
          <span>{t('plan.progress.total')} <span className="text-slate-300 font-medium">{fmt(totalSaved, currency)}</span></span>
          <span className="text-brand-400 font-semibold">{pct.toFixed(1)}%</span>
        </div>
        <div className="h-2.5 bg-surface rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-500 rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
        <p className="text-xs text-slate-500">
          {entries} {entries === 1 ? t('plan.progress.entry') : t('plan.progress.entries')} logged —{' '}
          <Link to={`/plans/${planId}/progress`} className="text-brand-400 hover:text-brand-300 transition-colors">
            {t('plan.progress.log')}
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function PlanPage() {
  const { id } = useParams();
  const { isPaid, user } = useAuthStore();
  const t = useT();
  const paid = isPaid();
  const currency = user?.currency || 'BRL';

  const { data: plan, isLoading, error } = usePlan(id);
  const { mutateAsync: regenerate, isPending: isRegenerating } = useRegeneratePlan();
  const { mutateAsync: updateGoal, isPending: isUpdatingGoal } = useUpdateGoal();
  const { data: goal } = useGoal(plan?.goal_id);
  const { data: progressData } = usePlanProgress(id);
  const [isExporting, setIsExporting] = useState(false);
  const [showRegenModal, setShowRegenModal] = useState(false);

  async function handleRegenSame() {
    setShowRegenModal(false);
    regenerate(plan.id);
  }

  async function handleRegenWithUpdate(formData) {
    await updateGoal({ id: plan.goal_id, ...formData });
    setShowRegenModal(false);
    regenerate(plan.id);
  }

  async function handleExportPdf() {
    if (!paid) return;
    setIsExporting(true);
    try {
      exportPlanPdf(plan, progressData, currency);
    } catch {
      toast.error('Failed to generate PDF.');
    } finally {
      setIsExporting(false);
    }
  }

  const MAX_SIMS = paid ? parseInt(import.meta.env.VITE_MAX_PROJECTION_SIMULATIONS ?? '3', 10) : 1;
  const [simulations, setSimulations] = useState([]);

  function handleToggleSimulation(instrument) {
    const isActive = simulations.some((s) => s.name === instrument.name);
    if (isActive) {
      setSimulations((prev) => prev.filter((s) => s.name !== instrument.name));
    } else if (simulations.length >= MAX_SIMS) {
      toast.info(`Only ${MAX_SIMS} simulations can be shown at once. Remove one first.`);
    } else {
      setSimulations((prev) => [...prev, {
        name: instrument.name,
        monthly_rate_pct: instrument.monthly_rate_pct,
        annual_rate_pct: instrument.annual_rate_pct,
      }]);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-surface-card rounded-xl w-72" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-28 bg-surface-card rounded-2xl" />)}
        </div>
        <div className="h-64 bg-surface-card rounded-2xl" />
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-400">{t('plan.failed.load')} <Link to="/goals" className="underline">{t('plan.breadcrumb.goals')}</Link></p>
      </div>
    );
  }

  // ── Generating state ────────────────────────────────────────────────────
  if (plan.status === 'generating') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 animate-fade-in">
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full border-4 border-brand-600/30" />
          <div className="absolute inset-0 rounded-full border-4 border-brand-500 border-t-transparent animate-spin" />
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-100">{t('plan.generating.title')}</h1>
          <p className="text-slate-400 mt-2 max-w-sm">
            {t('plan.generating.sub')}
          </p>
          <p className="text-slate-500 text-sm mt-4">{t('plan.generating.notify')}</p>
        </div>
        <Link to="/goals" className="text-brand-400 hover:text-brand-300 text-sm transition-colors">
          {t('plan.generating.back')}
        </Link>
      </div>
    );
  }

  // ── Failed state ────────────────────────────────────────────────────────
  if (plan.status === 'failed') {
    return (
      <div className="card text-center py-14 max-w-lg mx-auto">
        <p className="text-4xl mb-4">⚠️</p>
        <h2 className="text-lg font-bold text-red-400 mb-2">{t('plan.failed.title')}</h2>
        <p className="text-slate-400 text-sm mb-6">
          {plan.error_message || t('plan.failed.sub')}
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => regenerate(plan.id)}
            disabled={isRegenerating}
            className="btn-primary text-sm"
          >
            {isRegenerating ? t('plan.failed.retrying') : t('plan.failed.retry')}
          </button>
          <Link to="/goals" className="btn-secondary text-sm">{t('plan.failed.back')}</Link>
        </div>
      </div>
    );
  }

  const goalType = t(`goal.type.${plan.goal_type}`) || plan.goal_type;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500 mb-1">
            <Link to="/goals" className="hover:text-slate-300 transition-colors">{t('plan.breadcrumb.goals')}</Link>
            {' / '}
            <span className="text-slate-400">{goalType}</span>
          </p>
          <h1 className="text-2xl font-bold text-slate-100">{plan.goal_title}</h1>
          {plan.summary && <p className="text-slate-400 mt-1 max-w-xl">{plan.summary}</p>}
        </div>
        <Button
          variant="secondary"
          size="sm"
          isLoading={isRegenerating}
          onClick={() => { if (paid) setShowRegenModal(true); }}
          disabled={!paid}
          title={!paid ? t('plan.regenerate.pro_only') : undefined}
        >
          {t('plan.regenerate')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          isLoading={isExporting}
          onClick={handleExportPdf}
          disabled={!paid}
          title={!paid ? t('plan.export_pdf.pro_only') : undefined}
        >
          ⬇ {t('plan.export_pdf')}
        </Button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label={t('plan.stat.cost')}
          value={fmt(plan.effective_cost, currency)}
          sub={plan.ai_estimated_cost ? t('plan.stat.cost.ai') : t('plan.stat.cost.user')}
          highlight
        />
        <SummaryCard
          label={t('plan.stat.monthly')}
          value={fmt(plan.monthly_savings_target, currency)}
          sub={t('plan.stat.monthly.sub')}
        />
        <SummaryCard
          label={t('plan.stat.time')}
          value={`${plan.estimated_months || '?'} mo`}
          sub={t('plan.stat.time.sub')}
        />
        <SummaryCard
          label={t('plan.stat.risk')}
          value={plan.risk_profile ? plan.risk_profile.charAt(0).toUpperCase() + plan.risk_profile.slice(1) : '—'}
          sub={t('plan.stat.risk.sub')}
        />
      </div>

      {/* AI summary box */}
      {plan.ai_reasoning && (
        <div className="rounded-xl border border-brand-800/40 bg-brand-950/20 p-5">
          <p className="text-xs font-semibold text-brand-400 uppercase tracking-wider mb-2">{t('plan.ai_reasoning')}</p>
          <p className="text-slate-300 text-sm leading-relaxed">{plan.ai_reasoning}</p>
        </div>
      )}

      {/* Scenarios chart — paid only */}
      {paid && plan.scenarios?.length > 0 ? (
        <ScenarioChart
          scenarios={plan.scenarios}
          effectiveCost={plan.effective_cost}
          simulations={simulations}
          projection={plan.monthly_projection}
          currency={currency}
        />
      ) : !paid && (
        <div className="card text-center py-10 border-dashed">
          <p className="text-2xl mb-2">📊</p>
          <p className="text-slate-300 font-medium">{t('plan.scenarios.pro_title')}</p>
          <p className="text-slate-500 text-sm mt-1 mb-4">
            {t('plan.scenarios.pro_sub')}
          </p>
          <a href="/pricing" className="btn-primary inline-block text-sm">
            {t('plan.scenarios.pro_cta')}
          </a>
        </div>
      )}

      {/* AI Insights */}
      {(plan.risk_warnings?.length > 0 || plan.recommendations?.length > 0) && (
        <AIInsights
          riskWarnings={plan.risk_warnings}
          recommendations={plan.recommendations}
          currency={currency}
        />
      )}

      {/* Monthly projection table */}
      {plan.monthly_projection?.length > 0 && (
        <MonthlyProjectionTable
          projection={plan.monthly_projection}
          simulations={simulations}
          currency={currency}
          planCreatedAt={plan.created_at}
          progressEntries={progressData?.entries}
        />
      )}

      {/* Investment Education */}
      {plan.investment_education && (
        <InvestmentEducation
          education={plan.investment_education}
          currency={currency}
          simulations={simulations}
          onToggleSimulation={handleToggleSimulation}
          maxSims={MAX_SIMS}
          paid={paid}
        />
      )}

      {/* Progress summary */}
      <ProgressSummaryCard planId={plan.id} currency={currency} />

      {/* Regenerate modal */}
      <Modal
        isOpen={showRegenModal}
        onClose={() => setShowRegenModal(false)}
        title={t('regen_modal.title')}
        size="lg"
      >
        <GoalForm
          initialValues={goal}
          loading={isRegenerating || isUpdatingGoal}
          onUseSame={handleRegenSame}
          onSubmit={handleRegenWithUpdate}
        />
      </Modal>
    </div>
  );
}
